import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Wallet, Gift, Gamepad2, ArrowDownCircle, ArrowUpCircle, X, Send, QrCode, ArrowLeftRight, ChevronDown } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { formatFP } from '../../../lib/money';
import WalletTransferPanel from './WalletTransferPanel';

/**
 * Modal panel showing the player's full balance breakdown:
 * - Wallet (cash, USD)
 * - Freeplay credits (FP) — display only; game-card rules control cashouts
 * - Total game balance
 * - Deposit / Cashout shortcuts
 */
export default function BalanceDetailPanel({ wallet, accounts, games, onPurchase, onCashout, onWalletChanged, onVerificationRequired, close }) {
  const [transferMode, setTransferMode] = useState('');
  const [transferChoicesOpen, setTransferChoicesOpen] = useState(false);
  const walletUsd = Number(wallet?.cash_balance ?? wallet?.balance ?? 0);
  const gameLines = useMemo(() => {
    const seen = new Set();
    return accounts.filter(a => {
      const accountId = a.provider_account_id || a.provider_user_id || a.username || a.game_username || '';
      const rawKey = [
        a.provider_code || a.game_code || '',
        a.provider_game_id || a.game_id || a.game_code || '',
        accountId,
      ].join('|').toLowerCase() || `id:${a.id || ''}`;
      const key = rawKey.replace(/\|/g, '') ? rawKey : `id:${a.id || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map(a => {
      const isCustom = a.is_custom || a.provider_code === 'manual';
      const game = isCustom
        ? { id: a.game_id, name: a.game_name || 'Custom Game' }
        : (games.find(g => g.id === a.game_id || g.code === a.game_code) || { name: a.game_name || 'Game' });
      return {
        id: a.id,
        name: game.name,
        balance: Number(a.last_known_balance ?? a.balance_usd ?? 0),
        custom: isCustom,
      };
    });
  }, [accounts, games]);
  const totalInGames = gameLines.reduce((s, g) => s + g.balance, 0);
  const keepTransferClickInside = (event) => {
    event?.stopPropagation?.();
    event?.nativeEvent?.stopImmediatePropagation?.();
  };

  return (
    <div className="flex h-[100dvh] flex-col bg-white" data-testid="balance-detail-panel">
      <div className="shrink-0 px-5 pt-[calc(env(safe-area-inset-top)+1.25rem)] pb-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <div>
          <h3 className="display text-xl font-extrabold" style={{ color: '#0B0F0C' }}>Your Balances</h3>
          <p className="text-xs" style={{ color: '#6B7280' }}>Total across wallet & games</p>
        </div>
        <button onClick={close} className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: '#F3F4F6', color: '#0B0F0C' }}>
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-3 pb-28">
        {/* Wallet balance */}
        <div className="rounded-2xl p-4 flex items-center gap-3" style={{ background: 'linear-gradient(135deg, #DCFCE7, #F0FDF4)', border: '1px solid rgba(34,197,94,0.25)' }} data-testid="balance-wallet-card">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'radial-gradient(circle at 30% 30%, #B6FFD3, #22FF88 45%, #008F44 95%)', boxShadow: 'inset 0 0 10px rgba(255,255,255,0.4), 0 0 14px rgba(34,255,136,0.45)' }}>
            <Wallet className="w-6 h-6" style={{ color: '#02140A' }} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#15803D' }}>Wallet Balance</p>
            <p className="display break-all text-2xl font-extrabold" style={{ color: '#0B0F0C' }}>${walletUsd.toFixed(2)}</p>
          </div>
        </div>

        {/* FP card */}
        <div
          className="w-full rounded-2xl p-4 flex items-center gap-3 text-left"
          style={{
            background: 'linear-gradient(135deg, #FEF3C7, #FFFBEB)',
            border: '1px solid rgba(250,204,21,0.45)',
            boxShadow: '0 4px 14px rgba(250,204,21,0.18)',
          }}
          data-testid="balance-fp-card"
          aria-label="Freeplay credits"
        >
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: 'radial-gradient(circle at 30% 30%, #FFF6B0, #FACC15 45%, #B45309 95%)',
              boxShadow: 'inset 0 0 10px rgba(255,255,255,0.4), 0 0 14px rgba(250,204,21,0.55)',
            }}
          >
            <Gift className="w-6 h-6" style={{ color: '#3F2A00' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#92400E' }}>
              Freeplay Credits
            </p>
            <p className="display break-all text-2xl font-extrabold" style={{ color: '#0B0F0C' }} data-testid="balance-fp-value">
              {formatFP(wallet?.play_credits ?? wallet?.fp_credits ?? 0)} <span className="text-base font-bold" style={{ color: '#92400E' }}>FP</span>
            </p>
            <p className="text-[11px] mt-0.5 font-bold" style={{ color: '#B45309' }}>
              Load FP into a game to play. Cashout rules appear on the game card.
            </p>
          </div>
        </div>

        {/* Games total */}
        <div className="rounded-2xl p-4" style={{ background: 'linear-gradient(135deg, #EEF2FF, #F8FAFC)', border: '1px solid rgba(99,102,241,0.25)' }} data-testid="balance-games-card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6366F1, #4338CA)' }}>
              <Gamepad2 className="w-6 h-6" style={{ color: '#fff' }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#4338CA' }}>Total in Games</p>
              <p className="display break-all text-2xl font-extrabold" style={{ color: '#0B0F0C' }}>${totalInGames.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setTransferChoicesOpen((open) => !open)}
          className="w-full rounded-2xl p-4 flex items-center gap-3 transition active:scale-[0.99] hover:brightness-105 text-left"
          style={{
            background: 'linear-gradient(135deg, #F5F3FF, #EFF6FF)',
            border: '1px solid rgba(124,58,237,0.24)',
            boxShadow: '0 4px 14px rgba(79,70,229,0.12)',
          }}
          data-testid="balance-internal-transfer-card"
          aria-expanded={transferChoicesOpen}
        >
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: 'linear-gradient(135deg, #8B5CF6, #2563EB)',
              boxShadow: 'inset 0 0 10px rgba(255,255,255,0.22), 0 0 14px rgba(99,102,241,0.35)',
            }}
          >
            <ArrowLeftRight className="w-6 h-6" style={{ color: '#fff' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#5B21B6' }}>
              Internal Transfer
            </p>
            <p className="text-xs mt-0.5 font-semibold" style={{ color: '#475569' }}>
              Send and receive balance from friends and family
            </p>
          </div>
          <ChevronDown
            className={`w-5 h-5 shrink-0 transition-transform ${transferChoicesOpen ? 'rotate-180' : ''}`}
            style={{ color: '#5B21B6' }}
          />
        </button>

        {transferChoicesOpen && (
          <div className="grid grid-cols-2 gap-2" data-testid="balance-transfer-actions">
            <Button
              type="button"
              onClick={() => setTransferMode('send')}
              className="h-12 rounded-xl display font-bold"
              style={{ background: 'linear-gradient(180deg,#3B82F6,#1D4ED8)', color: '#fff' }}
              data-testid="wallet-transfer-open-send"
            >
              <Send className="w-4 h-4 mr-1" /> Send
            </Button>
            <Button
              type="button"
              onClick={() => setTransferMode('receive')}
              className="h-12 rounded-xl display font-bold"
              style={{ background: 'linear-gradient(180deg,#8B5CF6,#6D28D9)', color: '#fff' }}
              data-testid="wallet-transfer-open-receive"
            >
              <QrCode className="w-4 h-4 mr-1" /> Receive
            </Button>
          </div>
        )}
      </div>

      <div
        className="shrink-0 px-5 pt-3 pb-[calc(env(safe-area-inset-bottom)+1rem)] space-y-2"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.86), #FFFFFF 24%)',
          borderTop: '1px solid rgba(15,23,42,0.08)',
          boxShadow: '0 -12px 28px rgba(15,23,42,0.08)',
        }}
      >
        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={onPurchase}
            className="balance-action-btn balance-action-deposit h-12 display font-bold rounded-xl"
            style={{
              background: 'linear-gradient(180deg,#28E879,#159447)',
              color: '#fff',
              border: '1px solid rgba(34,255,136,0.45)',
              boxShadow: '0 0 0 1px rgba(34,255,136,0.18), 0 10px 24px rgba(21,148,71,0.38), 0 0 18px rgba(34,255,136,0.42), inset 0 1px 0 rgba(255,255,255,0.26)',
            }}
            data-testid="balance-purchase"
          >
            <ArrowDownCircle className="balance-action-icon w-4 h-4 mr-1" /> Deposit
          </Button>
          <Button
            onClick={onCashout}
            className="balance-action-btn balance-action-cashout h-12 display font-bold rounded-xl"
            style={{
              background: 'linear-gradient(180deg,#FF7A1A,#D33A05)',
              color: '#fff',
              border: '1px solid rgba(249,115,22,0.48)',
              boxShadow: '0 0 0 1px rgba(249,115,22,0.18), 0 10px 24px rgba(194,65,12,0.38), 0 0 18px rgba(249,115,22,0.44), inset 0 1px 0 rgba(255,255,255,0.26)',
            }}
            data-testid="balance-cashout"
          >
            <ArrowUpCircle className="balance-action-icon w-4 h-4 mr-1" /> Cashout
          </Button>
        </div>
      </div>

      {transferMode && typeof document !== 'undefined' && createPortal((
        <div
          className="fixed inset-0 flex flex-col"
          data-wallet-transfer-layer="true"
          onPointerDown={keepTransferClickInside}
          onMouseDown={keepTransferClickInside}
          onTouchStart={keepTransferClickInside}
          onClick={keepTransferClickInside}
          style={{
            zIndex: 10000,
            width: '100vw',
            minHeight: '100dvh',
            background: '#FFFFFF',
            color: '#0B0F0C',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            pointerEvents: 'auto',
          }}
          data-testid="wallet-transfer-fullscreen"
        >
          <button
            type="button"
            onClick={() => setTransferMode('')}
            className="fixed top-4 right-4 z-[96] w-11 h-11 rounded-full flex items-center justify-center transition active:scale-95 hover:scale-105"
            style={{
              background: 'rgba(0,0,0,0.55)',
              border: '2px solid rgba(255,255,255,0.9)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
            }}
            data-testid="wallet-transfer-fullscreen-close"
            aria-label="Close Send and Receive"
          >
            <X className="w-5 h-5 text-white" strokeWidth={3} />
          </button>

          <div className="flex-1 overflow-y-auto">
            <div className="max-w-2xl mx-auto px-4 sm:px-8 py-10 sm:py-12">
              <div className="mb-5">
                <p className="text-xs uppercase tracking-[0.24em] font-extrabold" style={{ color: '#64748B' }}>
                  Wallet Transfer
                </p>
                <h3 className="display text-3xl font-extrabold mt-1" style={{ color: '#0B0F0C' }}>
                  {transferMode === 'send' ? 'Send Balance' : 'Receive Balance'}
                </h3>
              </div>

              <div
                className="mb-4 flex max-w-full flex-wrap items-center gap-2 rounded-xl px-3 py-2 min-[360px]:px-4"
                style={{ background: '#F0FDF4', border: '1px solid rgba(34,197,94,0.26)' }}
                data-testid="wallet-transfer-current-balance"
              >
                <Wallet className="h-4 w-4 shrink-0" style={{ color: '#15803D' }} />
                <span className="text-xs uppercase tracking-wider font-bold" style={{ color: '#15803D' }}>Current Balance:</span>
                <span className="display min-w-0 break-all font-extrabold mono" style={{ color: '#15803D' }}>${walletUsd.toFixed(2)}</span>
              </div>

              <WalletTransferPanel
                walletUsd={walletUsd}
                initialMode={transferMode}
                onWalletChanged={onWalletChanged}
                onVerificationRequired={onVerificationRequired}
                onClose={() => setTransferMode('')}
              />
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
